// src/ui/shareImage.ts
// Browser glue for the image share card: paint the pure layout onto a
// canvas, then share through the best channel the platform offers.
//
// Fallback chain (first success wins):
//   1. Web Share with the PNG as a file  (iOS/Android/desktop Safari+Chrome)
//   2. Web Share, text only
//   3. Clipboard: image/png + text/plain in one ClipboardItem (desktop Chrome/Safari)
//   4. Clipboard: text only
//   5. Toast telling the user to copy manually
// A dismissed share sheet (AbortError) ends the chain silently.

import { CARD_W, CARD_H, shareCardOps, paintOps, type ShareCardInput } from '../core/shareCard';

// async so that a synchronous throw from createElement / getContext / paintOps
// surfaces as a rejected promise instead of unwinding the caller's render.
export async function renderShareCard(input: ShareCardInput, doc: Document = document): Promise<Blob> {
  const canvas = doc.createElement('canvas');
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  paintOps(ctx, shareCardOps(input));
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('canvas toBlob produced no data'));
    }, 'image/png');
  });
}

export type ShareMethod = 'file' | 'text' | 'clipboard_image' | 'clipboard_text' | 'failed';

export type ShareDeps = {
  share?: (data: ShareData) => Promise<void>;
  canShare?: (data: ShareData) => boolean;
  clipboardWrite?: (items: ClipboardItem[]) => Promise<void>;
  clipboardWriteText?: (text: string) => Promise<void>;
  ClipboardItem?: typeof ClipboardItem;
  toast: (message: string) => void;
};

export function browserShareDeps(toast: (message: string) => void): ShareDeps {
  const nav = navigator;
  return {
    share: typeof nav.share === 'function' ? (d) => nav.share(d) : undefined,
    canShare: typeof nav.canShare === 'function' ? (d) => nav.canShare(d) : undefined,
    clipboardWrite: nav.clipboard && typeof nav.clipboard.write === 'function'
      ? (items) => nav.clipboard.write(items) : undefined,
    clipboardWriteText: nav.clipboard && typeof nav.clipboard.writeText === 'function'
      ? (t) => nav.clipboard.writeText(t) : undefined,
    ClipboardItem: typeof ClipboardItem === 'function' ? ClipboardItem : undefined,
    toast,
  };
}

function isAbort(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

export async function shareWithImage(text: string, blob: Blob | null, deps: ShareDeps): Promise<ShareMethod> {
  if (deps.share) {
    if (blob && deps.canShare) {
      const file = new File([blob], 'barve.png', { type: 'image/png' });
      const data: ShareData = { files: [file], text };
      let supported = false;
      try {
        supported = deps.canShare(data);
      } catch (err) {
        console.warn('[share]', 'canShare threw; treating as unsupported', err);
      }
      if (supported) {
        try {
          await deps.share(data);
          return 'file';
        } catch (err) {
          if (isAbort(err)) return 'failed';
          console.warn('[share]', 'file share failed; trying text share', err);
        }
      }
    }
    try {
      await deps.share({ text });
      return 'text';
    } catch (err) {
      if (isAbort(err)) return 'failed';
      console.warn('[share]', 'text share failed; trying clipboard', err);
    }
  }

  if (blob && deps.clipboardWrite && deps.ClipboardItem) {
    try {
      const item = new deps.ClipboardItem({
        'image/png': blob,
        'text/plain': new Blob([text], { type: 'text/plain' }),
      });
      await deps.clipboardWrite([item]);
      deps.toast('Copied result + image ✓');
      return 'clipboard_image';
    } catch (err) {
      console.warn('[share]', 'image clipboard write failed; trying text', err);
    }
  }

  if (deps.clipboardWriteText) {
    try {
      await deps.clipboardWriteText(text);
      deps.toast('Copied to clipboard ✓');
      return 'clipboard_text';
    } catch (err) {
      console.warn('[share]', 'text clipboard write failed', err);
    }
  }

  deps.toast('Could not copy — long-press the share text');
  return 'failed';
}
